import {
	type EventMappings,
	type LegacySegmentEventPropMappings,
	LegacySegmentTrackEvent,
	type PageDetails,
	type PagePerformance,
	type Track,
	type WorkflowId,
} from '@1debit/chime-schemas/typescript_web_analytics';

import { EventRouterHandler, type EventRouterHandlerConfig } from './EventRouterHandler';
import { type AppEventContext } from './EventRouterService';
import { SegmentHandler, type SegmentHandlerConfig } from './SegmentHandler';

type SupportedIframes = {
	segment?: boolean;
};

type DefaultConfig = EventRouterHandlerConfig & SegmentHandlerConfig;

export type AnalyticsConfig =
	| ({ variant: 'default' } & DefaultConfig)
	| ({ variant: 'onlyEventRouter' } & EventRouterHandlerConfig);

export class AnalyticsBase {
	requiredIframes: SupportedIframes = {};
	private segmentHandler: SegmentHandler | undefined;
	private eventRouterHandler: EventRouterHandler;

	constructor(config: AnalyticsConfig) {
		// This option is only needed for enrollment-client because it has it's own Segment implementation
		if (config.variant === 'onlyEventRouter') {
			this.eventRouterHandler = new EventRouterHandler(config);
		} else {
			this.segmentHandler = new SegmentHandler(config);
			this.requiredIframes.segment = true;
			this.eventRouterHandler = new EventRouterHandler(config);
		}
	}

	init(eventRouterAppContext?: AppEventContext, segmentIframeElement?: HTMLIFrameElement) {
		this.segmentHandler?.init(segmentIframeElement);
		setTimeout(() => {
			this.eventRouterHandler.init(eventRouterAppContext);
		}, 0);
	}

	identify(userId: number, properties?: Record<string, unknown>) {
		this.eventRouterHandler.identify(userId, properties);
		this.segmentHandler?.identify(userId, properties);
	}

	reset() {
		this.segmentHandler?.reset();
		this.eventRouterHandler.reset();
	}

	trackPage(pageName: string, pageDetails?: PageDetails, pagePerformance?: PagePerformance) {
		this.segmentHandler?.trackPage(pageName, pageDetails as Record<string, unknown>);
		this.eventRouterHandler.trackPage(pageName, pageDetails, pagePerformance);
	}

	trackEvent<EventName extends Exclude<Track, Track.WebPageViewed>>(
		eventName: EventName,
		properties: EventMappings[EventName],
	) {
		this.eventRouterHandler.trackEvent(eventName, properties);
	}

	// Deprecated events, we will eventually migrate them to use app router
	trackElementClicked(properties: LegacySegmentEventPropMappings[LegacySegmentTrackEvent.ElementClicked]) {
		this.segmentHandler?.trackEvent(LegacySegmentTrackEvent.ElementClicked, properties);
	}

	trackActionTriggered(properties: LegacySegmentEventPropMappings[LegacySegmentTrackEvent.ActionTriggered]) {
		this.segmentHandler?.trackEvent(LegacySegmentTrackEvent.ActionTriggered, properties);
	}

	trackBannerShown(properties: LegacySegmentEventPropMappings[LegacySegmentTrackEvent.WebBannerShown]) {
		this.segmentHandler?.trackEvent(LegacySegmentTrackEvent.WebBannerShown, properties);
	}

	trackViewedFullScreenModal(
		properties: LegacySegmentEventPropMappings[LegacySegmentTrackEvent.ViewedFullScreenModal],
	) {
		this.segmentHandler?.trackEvent(LegacySegmentTrackEvent.ViewedFullScreenModal, properties);
	}

	startWorkflow(workflowId: WorkflowId) {
		this.eventRouterHandler.startWorkflow(workflowId);
	}

	endWorkflow(workflowId: WorkflowId) {
		this.eventRouterHandler.endWorkflow(workflowId);
	}
}
